import { Directive, HostListener, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Matches markup only when the text both opens and closes a tag. A looser test
 * would turn ordinary prose containing "<" (say "a < b") into an HTML paste.
 */
const LOOKS_LIKE_HTML = /<([a-z][a-z0-9-]*)(\s[^>]*)?>/i;
const HAS_CLOSING_TAG = /<\/[a-z][a-z0-9-]*\s*>/i;

/**
 * Lets Quill fields accept HTML markup pasted as plain text.
 *
 * Quill only treats a paste as rich content when the clipboard carries a
 * `text/html` flavour. Copying markup out of a code editor, an email source or
 * a CMS puts it on the clipboard as `text/plain`, so Quill inserts it verbatim
 * and the tags end up stored as literal characters.
 *
 * This intercepts that one case -- plain text that is clearly markup -- and
 * feeds it through Quill's HTML parser instead. Everything else (real rich
 * pastes, ordinary text) is left to Quill untouched.
 *
 * Applies to every `<quill-editor>` in a component that imports it, so the
 * behaviour can't drift between one form and another.
 */
@Directive({
  selector: 'quill-editor',
  standalone: true,
})
export class QuillHtmlPasteDirective {
  private readonly _sanitizer = inject(DomSanitizer);

  @HostListener('onEditorCreated', ['$event'])
  public onEditorCreated(quill: any): void {
    if (!quill?.root) {
      return;
    }

    // Capture phase so this runs before Quill's own paste handling and can
    // cancel it when we take over.
    quill.root.addEventListener(
      'paste',
      (event: ClipboardEvent) => this._onPaste(quill, event),
      true
    );
  }

  private _onPaste(quill: any, event: ClipboardEvent): void {
    const clipboard = event.clipboardData;
    if (!clipboard) {
      return;
    }

    // A real rich-text paste already carries text/html -- Quill handles that
    // better than we would.
    if (clipboard.getData('text/html')) {
      return;
    }

    const text = clipboard.getData('text/plain');
    if (!text || !this._isHtml(text)) {
      return;
    }

    // Pasted markup is untrusted: strip scripts, event handlers and anything
    // else Angular considers unsafe before it reaches the document.
    const safeHtml = this._sanitizer.sanitize(SecurityContext.HTML, text);
    if (!safeHtml) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const selection = quill.getSelection(true);
    const index = selection?.index ?? quill.getLength();

    if (selection?.length) {
      quill.deleteText(index, selection.length, 'user');
    }

    // Quill still filters this down to the formats the toolbar supports, so
    // the stored value stays within the editor's own vocabulary.
    quill.clipboard.dangerouslyPasteHTML(index, safeHtml, 'user');
  }

  private _isHtml(text: string): boolean {
    const trimmed = text.trim();
    return LOOKS_LIKE_HTML.test(trimmed) && HAS_CLOSING_TAG.test(trimmed);
  }
}
